import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useStore } from "../context/StoreContext";
import { api } from "../lib/api";
import { t } from "../lib/i18n";
import { ProductCard } from "../components/store/ProductCard";
import { Button } from "../components/ui/button";
import { Truck, ShieldCheck, MapPin, Package, Sparkles } from "lucide-react";

export default function Home() {
  const { lang } = useStore();
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 8)));
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="hero-pattern text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-20 md:py-32 grid md:grid-cols-2 gap-10 items-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-12 bg-[color:var(--thiqa-gold)]" />
              <span className="text-xs tracking-[0.3em] uppercase" style={{ color: "var(--thiqa-gold-soft)" }}>{t(lang, "tagline")}</span>
            </div>
            <h1 className="font-logo text-6xl md:text-8xl mb-6 leading-none" style={{ color: "var(--thiqa-gold-soft)" }}>
              الثقة
            </h1>
            <h2 className={`${lang === "ar" ? "font-arabic-heading" : "font-english-heading"} text-3xl md:text-5xl font-semibold mb-6 leading-tight`}>
              {t(lang, "hero_title")}
            </h2>
            <p className="text-base md:text-lg text-white/85 mb-8 max-w-lg leading-relaxed">{t(lang, "hero_sub")}</p>
            <div className="flex gap-3 flex-wrap">
              <Link to="/products" data-testid="hero-shop-now">
                <Button className="btn-gold rounded-full px-8 py-6 text-base font-semibold">{t(lang, "shop_now")}</Button>
              </Link>
              <a href="#categories" data-testid="hero-browse-cats">
                <Button variant="outline" className="rounded-full px-8 py-6 text-base bg-transparent border-white/40 text-white hover:bg-white/10">
                  {t(lang, "view_categories")}
                </Button>
              </a>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }} className="hidden md:block">
            <div className="grid grid-cols-2 gap-4">
              <img src="https://images.unsplash.com/photo-1519676867240-f03562e64548?w=400&q=85" alt="" className="rounded-2xl aspect-square object-cover" />
              <img src="https://images.unsplash.com/photo-1660394585016-508f949df960?w=400&q=85" alt="" className="rounded-2xl aspect-square object-cover mt-8" />
              <img src="https://images.unsplash.com/photo-1587049352846-4a222e784d38?w=400&q=85" alt="" className="rounded-2xl aspect-square object-cover" />
              <img src="https://images.unsplash.com/photo-1772474587292-08b3e8932acd?w=400&q=85" alt="" className="rounded-2xl aspect-square object-cover mt-8" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="border-b border-[#e8ddc9] bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: MapPin, label: t(lang, "from_istanbul") },
            { icon: Truck, label: t(lang, "fast_ship") },
            { icon: ShieldCheck, label: t(lang, "secure_pay") },
            { icon: Package, label: lang === "ar" ? "تجميع شحنات" : "Consolidated Shipping" },
          ].map((f, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#faf3e3] flex items-center justify-center">
                <f.icon size={18} style={{ color: "var(--thiqa-primary)" }} />
              </div>
              <span className="text-sm font-medium text-gray-700">{f.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section id="categories" className="max-w-7xl mx-auto px-6 md:px-8 py-16 md:py-24">
        <div className="text-center mb-12">
          <div className="ornament max-w-xs mx-auto mb-4"><Sparkles size={16} /></div>
          <h2 className={`${lang === "ar" ? "font-arabic-heading" : "font-english-heading"} text-3xl md:text-5xl font-semibold`} style={{ color: "var(--thiqa-primary)" }}>
            {t(lang, "categories")}
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {categories.map((c, i) => (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
            >
              <Link
                to={`/products?category=${c.id}`}
                data-testid={`category-${c.id}`}
                className="group block relative aspect-square rounded-2xl overflow-hidden border border-[#e8ddc9]"
              >
                <img src={c.image} alt="" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-4 start-4 end-4 text-white">
                  <h3 className={`${lang === "ar" ? "font-arabic-heading" : "font-english-heading"} text-lg md:text-xl font-semibold`}>
                    {lang === "ar" ? c.name_ar : c.name_en}
                  </h3>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Featured */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="text-xs tracking-[0.3em] uppercase font-semibold" style={{ color: "var(--thiqa-gold)" }}>Selected</span>
              <h2 className={`${lang === "ar" ? "font-arabic-heading" : "font-english-heading"} text-3xl md:text-5xl font-semibold mt-2`} style={{ color: "var(--thiqa-primary)" }}>
                {t(lang, "featured")}
              </h2>
            </div>
            <Link to="/products" className="text-sm underline underline-offset-4 hover:text-[color:var(--thiqa-primary)]">
              {t(lang, "all_products")} →
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {featured.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
